import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { conns } from '@cfg/axiosConfig.tsx';
import {
  type EvaluateOrderDiscount,
  type PosPromotionPreviewResponse,
} from '@eshop/types/offers.types.ts';

import type { ManualDiscountDraft, PosSaleLine } from '../types';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { DEFAULT_CURRENCY } from '@cfg/defines.ts';

export type PromotionBadgeTone = 'default' | 'success' | 'warning' | 'info';

export interface PromotionBadge {
  id: string;
  label: string;
  tone?: PromotionBadgeTone;
  description?: string;
}

export interface UsePromotionsBridgeOptions {
  lines: PosSaleLine[];
  orderDiscount?: ManualDiscountDraft | null;
  enabled?: boolean;
  customerId?: string | number | null;
  couponCode?: string | null;
  currency?: string;
}

export interface UsePromotionsBridgeResult {
  badges: PromotionBadge[];
  preview?: PosPromotionPreviewResponse;
  isRefreshing: boolean;
  lastEvaluatedAt: number | null;
  refresh: () => void;
  acceptPreview?: (preview: PosPromotionPreviewResponse) => void;
  error: Error | null;
}

const evaluatePromotions = async (payload: Record<string, unknown>): Promise<PosPromotionPreviewResponse> => {
  const { data } = await conns<PosPromotionPreviewResponse>('erp:pos:promotions:evaluate').post(payload);
  return data;
};

const normalizeOrderDiscount = (
  discount?: ManualDiscountDraft | null,
): EvaluateOrderDiscount | null => {
  if (!discount) {
    return null;
  }
  return {
    mode: discount.mode,
    value: Number(discount.value ?? 0),
    reason: discount.reason ?? null,
    note: discount.note ?? null,
  };
};

const buildPromotionLines = (lines: PosSaleLine[]) =>
  lines
    .filter((line) => line.quantity > 0)
    .map((line) => {
      const price = Number(line.item.price?.value ?? 0);
      const categories = Array.isArray(line.item.category_ids)
        ? [...line.item.category_ids].map((id) => String(id)).sort()
        : [];
      const manual = line.manualDiscount;
      return {
        item_id: line.item.id,
        sku: line.item.sku ?? `item-${line.item.id}`,
        quantity: Number(line.quantity ?? 0),
        unit_price: price,
        categories,
        attributes: {},
        manual_discount_amount: manual?.mode === 'amount' ? Number(manual.value) : undefined,
        manual_discount_percent: manual?.mode === 'percentage' ? Number(manual.value * 100) : undefined,
      };
    });

const describeCouponError = (code: string): string => {
  switch (code) {
    case 'code_expired':
      return 'El cupón está vencido.';
    case 'max_uses_reached_global':
      return 'El cupón alcanzó el máximo de usos.';
    case 'max_uses_reached_user':
      return 'El cliente ya usó el cupón al máximo.';
    case 'cart_not_eligible':
      return 'El carrito no cumple las condiciones del cupón.';
    case 'customer_required':
      return 'Seleccioná un cliente para aplicar el cupón.';
    case 'code_invalid':
    default:
      return 'No se pudo aplicar el cupón ingresado.';
  }
};

const buildBadgesFromResponse = (
  response: PosPromotionPreviewResponse | undefined,
  currency: string,
): PromotionBadge[] => {
  if (!response) {
    return [];
  }

  const badges: PromotionBadge[] = [];

  for (const promo of response.applied) {
    if (!promo || promo.discount_total <= 0) {
      continue;
    }
    badges.push({
      id: `promo-${promo.promotion_id}`,
      label: promo.coupon_code ? `Cupón ${promo.coupon_code}` : promo.name,
      tone: 'success',
      description: getFormattedPrice(Number(promo.discount_total), currency),
    });
  }

  if (response.coupon_error) {
    badges.push({
      id: `coupon-error-${response.coupon_error}`,
      label: 'Cupón no aplicado',
      tone: 'warning',
      description: describeCouponError(response.coupon_error),
    });
  }

  if (response.coupon_status === 'valid_not_better') {
    badges.push({
      id: 'coupon-valid-not-better',
      label: 'Cupón válido',
      tone: 'info',
      description: 'La oferta actual ofrece un descuento mayor.',
    });
  }

  return badges;
};

export const usePromotionsBridge = (
  options: UsePromotionsBridgeOptions,
): UsePromotionsBridgeResult => {
  const { lines, orderDiscount, enabled = true, customerId, couponCode, currency } = options;
  const queryClient = useQueryClient();

  const promotionLines = useMemo(() => buildPromotionLines(lines), [lines]);
  const normalizedOrderDiscount = useMemo(
    () => normalizeOrderDiscount(orderDiscount ?? null),
    [orderDiscount],
  );
  const normalizedCustomerId = useMemo(() => {
    if (customerId === undefined || customerId === null) {
      return undefined;
    }
    return String(customerId);
  }, [customerId]);
  const normalizedCouponCode = useMemo(() => {
    const value = couponCode?.trim();
    return value ? value : undefined;
  }, [couponCode]);

  const currencyCode = useMemo(() => {
    if (currency) {
      return currency;
    }
    const fromLine = lines.find((line) => line.item.price?.currency)?.item.price?.currency;
    return fromLine ?? DEFAULT_CURRENCY;
  }, [currency, lines]);

  const shouldFetch = enabled && (promotionLines.length > 0 || normalizedOrderDiscount !== null);

  const request = useMemo<Record<string, unknown>>(() => ({
    lines: promotionLines,
    // Keep the legacy cart envelope during the transition; the ERP preview
    // endpoint reads `lines`, while older consumers/tests still inspect it.
    cart: {
      currency: currencyCode,
      items: promotionLines.map((line) => ({
        sku: lines.find((entry) => entry.item.id === line.item_id)?.item.sku ?? `item-${line.item_id}`,
        item_id: String(line.item_id),
        qty: line.quantity,
        unit_price: line.unit_price,
        categories: line.categories,
        attributes: line.attributes,
      })),
    },
    channel: 'erp',
    customer_id: normalizedCustomerId ? Number(normalizedCustomerId) : undefined,
    coupon_code: normalizedCouponCode,
    order_discount: normalizedOrderDiscount,
    currency: currencyCode,
  }), [lines, promotionLines, currencyCode, normalizedCustomerId, normalizedCouponCode, normalizedOrderDiscount]);

  const itemsKey = useMemo(
    () =>
      promotionLines
        .map(
          (item) =>
            `${item.item_id}:${item.quantity}:${item.unit_price}:${(item.categories ?? []).join(',')}:${item.manual_discount_amount ?? 'none'}:${item.manual_discount_percent ?? 'none'}`,
        )
        .join('|'),
    [promotionLines],
  );

  const orderDiscountKey = normalizedOrderDiscount
    ? `${normalizedOrderDiscount.mode}:${normalizedOrderDiscount.value}`
    : 'none';

  const queryKey = useMemo(
    () => [
      'pos',
      'promotions',
      'evaluate',
      itemsKey,
      orderDiscountKey,
      normalizedCustomerId ?? 'anon',
      normalizedCouponCode ?? 'no-coupon',
      currencyCode,
    ],
    [itemsKey, orderDiscountKey, normalizedCustomerId, normalizedCouponCode, currencyCode],
  );

  const { data, isFetching, refetch, error: queryError, dataUpdatedAt } = useQuery<PosPromotionPreviewResponse>({
    queryKey,
    queryFn: () => evaluatePromotions(request),
    enabled: shouldFetch,
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 0,
  });

  const badges = useMemo(
    () => buildBadgesFromResponse(data, currencyCode ?? DEFAULT_CURRENCY),
    [data, currencyCode],
  );

  const refresh = useCallback(() => {
    if (!shouldFetch) {
      return;
    }
    void refetch({ cancelRefetch: false });
  }, [refetch, shouldFetch]);

  const acceptPreview = useCallback(
    (preview: PosPromotionPreviewResponse) => {
      queryClient.setQueryData(queryKey, preview);
    },
    [queryClient, queryKey],
  );

  const error = queryError instanceof Error ? queryError : null;
  const lastEvaluatedAt = data ? dataUpdatedAt : null;

  return {
    badges,
    preview: data,
    isRefreshing: isFetching,
    lastEvaluatedAt,
    refresh,
    acceptPreview,
    error,
  };
};

export default usePromotionsBridge;
